import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import axios from "axios";
import Swal from "sweetalert2";
import mitt from "mitt";
import version from "./version.json";
const emitter = mitt();
import authService from "./services/auth.service";
import { applyTheme } from "./theme/applyTheme";
import { createClaimsApiUrlRewriter } from "@/utils/claimsApiUrl";
import {
  isAuthRefreshRequest,
  isSessionEndedError,
  redirectToGatewayLogin,
  shouldAttemptAuthRefresh,
} from "@/utils/authFlow";
import {
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
} from "@/utils/permissions";
import { fireTextToast } from "@/utils/textToast";
import "./styles/tokens.css";
import "./styles/theme-light.css";
import "./styles/bootstrap-compat.css";
import "./styles/claims-theme.css";
import "./styles/page-theme.css";

const app = createApp(App);

applyTheme();

const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Swal.stopTimer);
    toast.addEventListener("mouseleave", Swal.resumeTimer);
  },
});

const ErrorToast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 6000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Swal.stopTimer);
    toast.addEventListener("mouseleave", Swal.resumeTimer);
  },
});

app.config.globalProperties.emitter = emitter;
app.config.globalProperties.version = "v" + version.version;

app.mixin({
  methods: {
    $success(msg) {
      fireTextToast(Toast, "success", msg);
    },
    $error(msg, error = null) {
      if (error !== null) {
        if (axios.isCancel(error)) return;
        if (error.message === "error.response is undefined") return;
        console.log(error);
      }
      fireTextToast(ErrorToast, "error", msg);
    },
    $warning(msg) {
      fireTextToast(Toast, "warning", msg);
    },
    $info(msg) {
      fireTextToast(Toast, "info", msg);
    },
    $can(permissionName) {
      return hasPermission(this.$store.state.auth.user, permissionName);
    },
    $canAny(...permissions) {
      return hasAnyPermission(this.$store.state.auth.user, ...permissions);
    },
    $canAll(...permissions) {
      return hasAllPermissions(this.$store.state.auth.user, ...permissions);
    },
  },
});

import globalDirectives from "./directives.js";

import "viewerjs/dist/viewer.css";
import VueViewer from "v-viewer";

axios.defaults.withCredentials = true;

let cancelSource = axios.CancelToken.source();
const rewriteClaimsApiUrl = createClaimsApiUrlRewriter();

const requestInterceptor = (config) => {
  config.url = rewriteClaimsApiUrl(config.url);
  config.cancelToken = cancelSource.token;
  return config;
};

axios.interceptors.request.use(requestInterceptor);

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (!error.response) {
      return Promise.reject(error);
      // console.log("No error response");
      // return error;
    }

    if (error.response.status === 401) {
      if (isAuthRefreshRequest(error.config)) {
        // Prevent infinite loop if refresh token request fails
        console.log("Refresh token request failed, redirecting to login");
        redirectToGatewayLogin();
        return Promise.reject(error);
      }

      if (!shouldAttemptAuthRefresh(error.config)) {
        return Promise.reject(error);
      }

      // 401, attempt to refresh token
      error.config._retry = true;

      try {
        await authService.refreshToken();
      } catch (refreshError) {
        if (isSessionEndedError(refreshError)) {
          redirectToGatewayLogin();
        }

        return Promise.reject(refreshError);
      }

      return axios(error.config);
    }

    if (error.response.status === 403) {
      if (error.response.data !== undefined) {
        if (error.response.data["2fa"] === "required") {
          console.info("2FA IS REQUIRED");
          router.replace({
            name: "2fa",
            params: {
              route: router.currentRoute.name,
              params: router.currentRoute.params,
              first: true,
            },
          });
          return error;
        }
        if (error.response.data["2fa"] === "expired") {
          console.info("2FA IS REQUIRED");
          router.replace({
            name: "twoFactorAuth",
            params: {
              route: router.currentRoute.name,
              first: false,
            },
          });
          return error;
        }
      }
      console.log("Error Response is 403");
      return Promise.reject(error);
    }

    if (axios.isCancel(error)) {
      console.log("Error is due to cancellation by user.");
    }
    return Promise.reject(error);
    // return error;
  }
);

// Install request and response interceptors before routing or mounting can send requests.
app
  .use(router)
  .use(store)
  .use(VueViewer)
  .use(globalDirectives)
  .mixin({
    methods: {},
  })
  .mount("#app");
